import {gql} from '@apollo/client';

import BenchlingConfig from 'BenchlingConfig';
import {doQuerySafe, readQuerySafe} from 'graphql-client/ApolloWrappers';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import ExcludeByTypename from 'graphql-client/ExcludeByTypename';
import ExcludeInaccessible from 'graphql-client/ExcludeInaccessible';
import ExtractByTypename from 'graphql-client/ExtractByTypename';
import {isInaccessible} from 'graphql-client/InaccessibleInfo';
import {FetchBoxInfoForRowQuery} from 'notebook/structured-tables/row-updaters/__generated/FetchBoxInfoForRowQuery';
import {FetchPlateInfoForRowQuery} from 'notebook/structured-tables/row-updaters/__generated/FetchPlateInfoForRowQuery';
import {
  RefreshParentStorableCapacityQuery,
  RefreshParentStorableCapacityQueryVariables,
} from 'notebook/structured-tables/row-updaters/__generated/RefreshParentStorableCapacityQuery';
import {DirectLookupInfo, SampleLookupInfo} from 'notebook/structured-tables/StructuredTableConstants';

import {InaccessibleInfoGraphQL} from '../../../../graphql-fragments/auth/__generated/InaccessibleInfoGraphQL';
import {
  BulkRefreshParentStorableCapacityQuery,
  BulkRefreshParentStorableCapacityQueryVariables,
} from './__generated/BulkRefreshParentStorableCapacityQuery';

export type ParentStorableCapacityInfo = ExcludeByTypename<
  ExcludeInaccessible<RefreshParentStorableCapacityQuery['storable']>,
  'ContainerNode'
>;

export function isParentStorableCapacityInfo(
  modelInfo:
    | ParentStorableCapacityInfo
    | InaccessibleInfoGraphQL
    | ExtractByTypename<FetchPlateInfoForRowQuery['storable'], 'PlateNode'>
    | ExtractByTypename<FetchBoxInfoForRowQuery['storable'], 'BoxNode'>
): modelInfo is ParentStorableCapacityInfo {
  return modelInfo.hasOwnProperty('occupiedCapacity');
}

export function isUnfinalizedParentStorableSample(sample: SampleLookupInfo): boolean {
  return Boolean((sample as DirectLookupInfo).isUnfinalizedParentStorable);
}

export const BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY = gql`
  query BulkRefreshParentStorableCapacityQuery($parentStorableIds: [StrictString!]!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storables(ids: $parentStorableIds) {
      ...ParentStorableCapacityGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ParentStorableCapacity').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export const REFRESH_PARENT_STORABLE_CAPACITY_QUERY = gql`
  query RefreshParentStorableCapacityQuery($parentStorableId: StrictString!)
  @skipAudit
  @engComponent(name: INVENTORY) {
    storable(id: $parentStorableId) {
      ...ParentStorableCapacityGraphQL
      ...InaccessibleInfoGraphQL
    }
  }
  ${getFragmentWrapper('ParentStorableCapacity').fragment}
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

function _createInaccessibleInfoNode(apiIdentifier: string): InaccessibleInfoGraphQL {
  return {
    __typename: 'InaccessibleInfoNode',
    invalidId: apiIdentifier,
    canRequestAccess: false,
  };
}

export async function fetchParentStorableCapacityModel(
  apiIdentifier: string
): Promise<ParentStorableCapacityInfo | InaccessibleInfoGraphQL> {
  const response = await doQuerySafe<
    RefreshParentStorableCapacityQuery,
    RefreshParentStorableCapacityQueryVariables
  >(
    {query: REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
    {variables: {parentStorableId: apiIdentifier}, fetchPolicy: 'network-only'}
  );
  // Containers aren't ever valid, so just handle as an inaccessible info node for downstream typesafety
  if (response.storable.__typename === 'ContainerNode') {
    return _createInaccessibleInfoNode(apiIdentifier);
  }
  return response.storable;
}

export function bulkFetchParentStorableCapacityModel(
  apiIdentifiers: Array<string>
): Promise<Array<ParentStorableCapacityInfo | InaccessibleInfoGraphQL>> {
  return doQuerySafe<BulkRefreshParentStorableCapacityQuery, BulkRefreshParentStorableCapacityQueryVariables>(
    {query: BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
    {variables: {parentStorableIds: apiIdentifiers}, fetchPolicy: 'network-only'}
  ).then((response) =>
    response.storables.map((storable) =>
      storable.__typename === 'ContainerNode' ? _createInaccessibleInfoNode(storable.id) : storable
    )
  );
}

export function readParentStorableCapacityFromCache(
  apiIdentifier: string
): ParentStorableCapacityInfo | null {
  if (BenchlingConfig.ELN_USE_BULK_LIVE_DATA_QUERIES) {
    const data = readQuerySafe<
      BulkRefreshParentStorableCapacityQuery,
      BulkRefreshParentStorableCapacityQueryVariables
    >(
      {query: BULK_REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
      {variables: {parentStorableIds: [apiIdentifier]}, fetchPolicy: 'cache-only'}
    );
    if (
      data == null ||
      !data.storables[0] ||
      isInaccessible(data.storables[0]) ||
      data.storables[0].__typename === 'ContainerNode'
    ) {
      return null;
    }
    return data.storables[0];
  } else {
    const data = readQuerySafe<
      RefreshParentStorableCapacityQuery,
      RefreshParentStorableCapacityQueryVariables
    >(
      {query: REFRESH_PARENT_STORABLE_CAPACITY_QUERY},
      {variables: {parentStorableId: apiIdentifier}, fetchPolicy: 'network-only'}
    );
    if (data == null || isInaccessible(data.storable) || data.storable.__typename === 'ContainerNode') {
      return null;
    }
    return data.storable;
  }
}
